import axios, { AxiosError } from 'axios';
import { action, observable, runInAction } from 'mobx';
import { Notice } from '../CommonInterfaces';
import { API, ErrorResponse, POLL_API } from '../util/ApiUtil';
import ErrorUtil from '../util/ErrorUtil';
import { filterForwardParamsFromCurrentParams } from '../util/ForwardParamsUtil';
import { getUrlForComponent } from '../util/NavigationUtil';
import { getStorage } from '../util/StorageUtil';
import { runInTeliaRumService } from '../util/TeliaOtelUtil';
import { ProfileMapper } from './BaseLoginStore';
import GaDataStore from './GaDataStore';
import LanguageStore from './LanguageStore';
import MergeStore from './MergeStore';
import { MobileIdLoginCredentials } from './MobileIdLoginStore';
import PasskeyStore from './PasskeyStore';
import RememberMeStore from './RememberMeStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import { SmartIdLoginCredentials } from './SmartIdLoginStore';
import UiStore from './UiStore';

export type PollingStatus = 'SUCCESS' | 'PENDING' | 'FAILED';
export type Status = 'SUBMITTING' | 'CANCELLED' | 'REDIRECTING' | PollingStatus;
export type Service = 'mobile-id' | 'smart-id';
export type ServiceStatus = 'OK' | 'WARNING' | 'CRITICAL';

export interface AuthStatus {
  statusCode: PollingStatus;
  errorMessage: string;
  errorCode: string;
}

export interface PollableLoginResponse {
  success: boolean;
  pollableAuthId?: string;
  verificationCode?: string;
  mandates: boolean;
  notice?: Notice;
  redirectUrl?: string;
  component?: string;
  params?: any;
}

const mapServiceToGaAuthType = {
  'mobile-id': 'mobileId',
  'smart-id': 'smartId',
};

const mapServiceToPlumbrAuthType = {
  'mobile-id': 'MID',
  'smart-id': 'SID',
};

const MAX_POLL_RETRIES = 10;

export default abstract class BasePollableLoginStore {
  @observable accessor status: Status | undefined;
  @observable accessor verificationCode: string | undefined;
  @observable accessor statusNotice: ServiceStatus | undefined;

  public shouldSavePasskeyAfterLogin = false;
  private authId?: string;
  private pollCancelTokenSource = axios.CancelToken.source();
  private readyToFetchStatus = true;
  private pollErrorCount = 0;

  protected routeStore: RouteStore;
  protected uiStore: UiStore;
  private languageStore: LanguageStore;
  private rememberMeStore: RememberMeStore;
  private mergeStore: MergeStore;
  private passkeyStore: PasskeyStore;

  constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.uiStore = rootStore.uiStore;
    this.languageStore = rootStore.languageStore;
    this.rememberMeStore = rootStore.rememberMeStore;
    this.mergeStore = rootStore.mergeStore;
    this.passkeyStore = rootStore.passkeyStore;
    this.addResumeEventListeners();
  }

  private addResumeEventListeners = () => {
    document.addEventListener('visibilitychange', this.continuePolling);
    window.addEventListener('focus', this.continuePolling);
  };

  private continuePolling = () => {
    if (document.hidden) {
      return;
    }
    const authId = this.authId || getStorage().getItem(`${this.service}-auth-id`);
    const verificationCode = this.verificationCode || getStorage().getItem(`${this.service}-verification-code`);
    if (!this.authId && authId && verificationCode) {
      runInAction(() => {
        this.authId = authId;
        this.verificationCode = verificationCode;
        this.status = 'PENDING';
      });
    }
    if (authId && verificationCode) {
      setTimeout(this.startPolling, 5000);
    }
  };

  abstract get service(): Service;

  abstract get loginCredentials(): MobileIdLoginCredentials | SmartIdLoginCredentials;

  public async fetchServiceStatus() {
    if (this.readyToFetchStatus) {
      runInAction(() => {
        this.readyToFetchStatus = false;
      });
      try {
        this.statusNotice = (await API.get(`${this.service}/status`)).data.status;
        setTimeout(() => runInAction(() => (this.readyToFetchStatus = true)), 5000);
      } catch (e: Error | string | any) {
        ErrorUtil.pushErrorToTeliaRumAndLog(e);
        runInAction(() => {
          this.statusNotice = 'OK';
          this.readyToFetchStatus = true;
        });
      }
    }
  }

  @action.bound
  public async startLogin() {
    try {
      this.status = 'SUBMITTING';
      this.uiStore.hideNotices();
      this.pollErrorCount = 0;
      const response = await API.post(`/${this.service}/auth${this.params}`, this.loginCredentials);
      this.handleInitSuccess(response.data);
      setTimeout(async () => await this.startPolling(), 2000);
    } catch (e: Error | string | any) {
      this.handleLoginError(e);
    }
  }

  public async cancelLogin() {
    try {
      this.pollCancelTokenSource.cancel();
      await API.post(`/${this.service}/auth/cancel${this.params}`, {});
      runInAction(() => {
        this.status = 'CANCELLED';
      });
      this.reset();
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
    }
  }

  private async finishLogin(noErrors: boolean = true) {
    if (noErrors) {
      await runInTeliaRumService(`Autentimise lõpule viimine (${mapServiceToPlumbrAuthType[this.service]})`, async () => {
        try {
          const response = await API.post(`/${this.service}/auth/finish${this.params}`, {});
          this.uiStore.handleNotice(response);
          this.handleLoginSuccess(response.data);
        } catch (e: Error | string | any) {
          this.handleLoginError(e);
        }
      });
    }
  }

  @action.bound
  protected handleLoginError(e: AxiosError) {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    this.status = 'FAILED';
    this.uiStore.showErrorNoticeFromResponse(e.response);
    GaDataStore.loginStatus(false, mapServiceToGaAuthType[this.service], this.rememberMeStore.rememberMe);
  }

  @action.bound
  protected handleInitSuccess(loginResponse: PollableLoginResponse) {
    this.status = 'PENDING';
    this.authId = loginResponse.pollableAuthId;
    this.verificationCode = loginResponse.verificationCode;
    getStorage().setItem(`${this.service}-auth-id`, this.authId || '');
    getStorage().setItem(`${this.service}-verification-code`, this.verificationCode || '');
  }

  @action.bound
  protected async handleLoginSuccess(loginResponse: PollableLoginResponse) {
    this.status = 'SUCCESS';
    if (!!loginResponse.params) {
      this.mergeStore.mergeParams = loginResponse.params;
      if (loginResponse.params.mergeStage === ProfileMapper.MERGE_ACCESS_VERIFIED) {
        return this.mergeStore.showMergeConfirmationOnAccountPage();
      }
    }
    if (this.shouldSavePasskeyAfterLogin) {
      await this.passkeyStore.register();
      this.shouldSavePasskeyAfterLogin = false;
    }
    await this.passkeyStore.handleOfferRegisterModalLogic(() => {
      this.handleLoginSuccessContinue(loginResponse);
    });
  }

  protected handleLoginSuccessContinue(loginResponse: PollableLoginResponse) {
    if (loginResponse.redirectUrl) {
      this.status = 'REDIRECTING';
      return this.routeStore.route(loginResponse.redirectUrl);
    } else if (loginResponse.component) {
      this.status = 'REDIRECTING';
      this.routeStore.route(getUrlForComponent(loginResponse, this.routeStore.formForwardParams));
    }
    GaDataStore.loginStatus(true, mapServiceToGaAuthType[this.service], this.rememberMeStore.rememberMe);
  }

  private startPolling = async () => {
    if (this.status === 'PENDING') {
      runInTeliaRumService(`Kasutaja vastuse kontroll (${mapServiceToPlumbrAuthType[this.service]})`, async () => {
        try {
          runInAction(() => {
            this.pollCancelTokenSource = axios.CancelToken.source();
          });
          const authStatus: AuthStatus = (
            await POLL_API.get(`/${this.service}/auth/poll${this.params}`, {
              params: { authId: this.authId },
              cancelToken: this.pollCancelTokenSource.token,
            })
          ).data;
          await this.handlePollSuccess(authStatus);
        } catch (e: Error | string | any) {
          this.handlePollError(e);
        }
      });
    }
  };

  @action
  private async handlePollSuccess(authStatus: AuthStatus) {
    const { statusCode, errorMessage, errorCode } = authStatus;
    if (statusCode !== 'PENDING') {
      if (statusCode === 'SUCCESS') {
        this.status = 'REDIRECTING';
      }
      this.uiStore.showErrorNotice(errorCode, errorMessage);
      await this.finishLogin(!errorCode && !errorMessage);
      this.reset();
    } else {
      setTimeout(() => this.startPolling(), 2500);
    }
  }

  @action.bound
  private handlePollError(e: AxiosError) {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    if (this.status === 'SUCCESS' || this.status === 'REDIRECTING') {
      return;
    }
    if (!e.response && this.pollErrorCount <= MAX_POLL_RETRIES) {
      this.pollErrorCount++;
      setTimeout(() => this.startPolling(), 2500);
      return;
    }
    const authStatus: ErrorResponse = e.response?.data as ErrorResponse;
    if (!!authStatus) {
      const { errorMessage, errorCode } = authStatus;
      this.uiStore.showErrorNotice(errorCode, errorMessage);
    } else {
      this.uiStore.showErrorNoticeFromResponse(e.response);
    }
    this.status = 'FAILED';
    this.reset();
  }

  private get params() {
    const params = this.routeStore.decodedParams;
    if (params && !params.locale) {
      params.locale = this.languageStore.currentLanguage;
    }
    return filterForwardParamsFromCurrentParams({ ...params, rememberMe: this.rememberMeStore.rememberMe });
  }

  @action.bound
  private reset() {
    this.pollErrorCount = 0;
    this.authId = undefined;
    this.verificationCode = undefined;
    getStorage().removeItem(`${this.service}-auth-id`);
    getStorage().removeItem(`${this.service}-verification-code`);
  }
}
